import { Component, Input } from '@angular/core';
import { EmailConfig } from '@modules/admin/interfaces/admin-url.interface';
import { CommonModule } from '@angular/common';

/**
 * The `FooterContactComponent` provides contact-related links and email functionality.
 * It displays various contact options and allows users to open an email client.
 */
@Component({
  selector: 'footer-contact',
  templateUrl: './footer-contact.component.html',
  styles: [':host { display: contents}'],
  standalone: true,
  imports: [CommonModule],
})
export class FooterContactComponent {
  @Input() emailConfigData: EmailConfig[] = [];

  /**
   * Opens the user's default email client with a pre-filled email template.
   */
  openEmailClient(emailTemplate): void {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      // Try opening Outlook first (respects iOS configured app better)
      const outlookLink = this.buildOutlookLink(emailTemplate);
      window.location.href = outlookLink;

      // Fallback to mailto if Outlook is not installed
      setTimeout(() => {
        window.location.href = this.buildMailToLink(emailTemplate);
      }, 1200);
    } else {
      // Non-iOS devices use normal mailto
      const mailToLink = this.buildMailToLink(emailTemplate);
      window.location.href = mailToLink; // Opens the user's default email client
    }
  }

  /**
   * Builds a `mailto` link from the email template.
   * @param template - The email template containing `to`, `subject`, and `body` fields.
   * @returns A formatted `mailto` link.
   */
  buildMailToLink(template): string {
    const to = encodeURIComponent(template.to);
    const cc = encodeURIComponent(template.cc || '');
    const subject = encodeURIComponent(template.subject);

    let mailToLink = `mailto:${to}?subject=${subject}`;
    if (cc) {
      mailToLink += `&cc=${cc}`;
    }
    return mailToLink;
  }

  buildOutlookLink(template): string {
    const to = encodeURIComponent(template.to);
    const cc = encodeURIComponent(template.cc);
    const subject = encodeURIComponent(template.subject || '');
    const body = encodeURIComponent(template.body || '');
    return `ms-outlook://compose?to=${to}&cc=${cc}&subject=${subject}&body=${body}`;
  }
}
